import { useEffect, useState } from 'react';
import type { ActiveViewer } from '@sflx/shared';
import { presence } from '../lib/api';

const POLL_MS = 15_000;
/** Roughly two minutes of backed-off retries before the poll gives up. */
const MAX_POLL_FAILURES = 12;

export interface ActiveViewersState {
  viewers: ActiveViewer[];
}

/**
 * Poll who's currently streaming while `enabled` -- the read endpoint is
 * admin-gated, so a viewer polling it would just collect 403s. Same
 * chained-timeout-with-backoff shape as `useRecentErrors`.
 */
export function useActiveViewers(enabled: boolean): ActiveViewersState {
  const [viewers, setViewers] = useState<ActiveViewer[]>([]);

  useEffect(() => {
    if (!enabled) {
      setViewers([]);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let failures = 0;

    const tick = async () => {
      try {
        const result = await presence.list();
        if (cancelled) return;
        setViewers(result.viewers);
        failures = 0;
        timer = setTimeout(() => void tick(), POLL_MS);
      } catch {
        if (cancelled) return;
        failures += 1;
        if (failures > MAX_POLL_FAILURES) return;
        timer = setTimeout(() => void tick(), POLL_MS * Math.min(failures, 8));
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);

  return { viewers };
}
